import React from 'react';
import { LinkStripeAccountDto } from '../../types/stripe';
import useGetStripeAccount from './useGetStripeAccount';
import useLinkStripeAccount from './useLinkStripeAccount';
import useLoginStripeAccount from './useLoginStripeAccount';

const useStripeSetUp = ({
  refreshUrl,
  returnUrl,
}: Partial<LinkStripeAccountDto> = {}) => {
  const { isLoading, isFetching, data, refetch } = useGetStripeAccount({
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  });
  const { mutateAsync: linkAccount, isPending: isLinking } =
    useLinkStripeAccount();
  const { mutateAsync: loginAccount, isPending: isGettingLogin } =
    useLoginStripeAccount();

  const openUrl = React.useCallback((url: string) => {
    window.open(url, '_blank', 'noopener');
  }, []);

  const onStartSetup = React.useCallback(async () => {
    try {
      if (data?.isOnboardComplete) return;
      if (isLoading || isFetching || isLinking || isGettingLogin) return;
      const currentUrl = window.location.href;
      const response = await linkAccount({
        refreshUrl: refreshUrl || currentUrl,
        returnUrl: returnUrl || currentUrl,
      });
      if (!response) return;

      const { url } = response;

      openUrl(url);
    } catch (error) {
      // Do nothing
    }
  }, [
    data?.isOnboardComplete,
    isLoading,
    isFetching,
    isLinking,
    isGettingLogin,
    linkAccount,
    refreshUrl,
    returnUrl,
    openUrl,
  ]);

  const onViewStripeAccount = React.useCallback(async () => {
    try {
      if (isLoading || isFetching || isLinking || isGettingLogin) return;
      const response = await loginAccount();
      if (!response) return;

      const { url } = response;

      openUrl(url);
    } catch (error) {
      // Do nothing
    }
  }, [isLoading, isFetching, isLinking, isGettingLogin, loginAccount, openUrl]);

  return {
    data,
    refetch,
    isLoading,
    isFetching,
    isLinking,
    isGettingLogin,
    onStartSetup,
    onViewStripeAccount,
  };
};

export default useStripeSetUp;
